import * as Sentry from '@sentry/vue'
import type { App } from 'vue'

import { SESSION_KEY, USER_ID_COOKIE } from '@/constants/storage'
import { router } from '@/router'
import { getCookie } from '@/utils/cookie'
import { identify } from '@/utils/track'

/**
 * DSN публичный по устройству: он уезжает в браузер с каждым билдом, и
 * отправить по нему можно только события — читать чужие нельзя. Поэтому
 * держим его константой, а не в env: переменные CI уже приезжали пустыми
 * и уносили с собой прод, а молчащий Sentry заметить куда труднее, чем
 * отвалившуюся карту.
 */
const DSN =
  'https://54d2ec736c2fefc7b86d491b355e9e18@o4512248993087488.ingest.de.sentry.io/4512249034702928'

/** Доля сессий с трейсами. Воронку считаем событиями, трейсы — про скорость. */
const TRACES_RATE = 0.2

/** Записи сессий: каждая десятая, чтобы видеть обычный путь по карте. */
const REPLAY_RATE = 0.1

/**
 * Подключение Sentry. Вызывается до остальных плагинов — их ошибки на старте
 * иначе пройдут мимо.
 */
/**
 * Куда писать события. Локальная проверка идёт в тот же проект, что и прод —
 * отдельного для неё нет, поэтому помечаем её окружением: в Sentry она
 * отфильтровывается одним кликом и не мешается с живыми клиентами.
 */
function environment(): string {
  const { hostname } = window.location
  return hostname === 'localhost' || hostname === '127.0.0.1' ? 'local' : 'production'
}

/**
 * Id захода. Держим в `sessionStorage`, а не в переменной: иначе любая
 * перезагрузка страницы разрывала бы путь надвое, а в webview Telegram
 * она случается сама по себе. Без хранилища (приватный режим) просто
 * заводим новый — это лучше, чем падать на телеметрии.
 */
function sessionId(): string {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY)
    if (saved) return saved

    const id = crypto.randomUUID()
    sessionStorage.setItem(SESSION_KEY, id)
    return id
  } catch {
    return crypto.randomUUID()
  }
}

export function initSentry(app: App): void {
  /*
   * Только собранный прод: с dev-сборки летят падения HMR и наши же опыты,
   * в которых тонут настоящие ошибки клиентов. Проверить локально можно
   * через `npm run build && npm run preview` — там это уже прод.
   */
  if (!import.meta.env.PROD) return

  Sentry.init({
    app,
    dsn: DSN,
    environment: environment(),
    /* Роутер отдаёт переходы между экранами: иначе все они — одна страница */
    integrations: [Sentry.browserTracingIntegration({ router })],
    tracesSampleRate: TRACES_RATE,
    replaysSessionSampleRate: REPLAY_RATE,
    /* Сессию с ошибкой записываем всегда — ради неё запись и нужна */
    replaysOnErrorSampleRate: 1,
  })

  /*
   * Клиента отмечаем сразу, до первого события: иначе ошибки первых секунд
   * останутся ничьими. Дальше id обновит `setUserId`, когда клиент заведётся
   * у бэкенда.
   */
  identify(getCookie(USER_ID_COOKIE))

  /*
   * Метка одного захода. По клиенту путь не собрать: он заходит много раз,
   * и всё сливается в кашу. По этому id в логах видно ровно один заход —
   * от открытия до заказа или до места, где человек ушёл.
   */
  Sentry.setAttribute('session.id', sessionId())

  /*
   * Запись сессий догружается отдельным чанком: на критическом пути её ~40 КБ
   * стоят дороже, чем первые секунды записи. Карта и оформление — куда она и
   * нужна — открываются заметно позже.
   */
  void import('@/plugins/sentryReplay').then((module) => module.addReplay())
}
